import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  achievementsApi,
  type StreakView,
  type UserAchievementView,
} from '../services/achievements.service';
import {
  formatProgressLabel,
  getEventIcon,
  getStreakTheme,
  getTierTheme,
} from '../components/achievements/achievementTheme';

const STATUS_BADGE: Record<UserAchievementView['status'], { label: string; bg: string; color: string }> = {
  locked: { label: 'Bloqueado', bg: 'bg-surface-container', color: 'text-on-surface-variant' },
  available: { label: 'Disponible', bg: 'bg-primary/10', color: 'text-primary' },
  in_progress: { label: 'En progreso', bg: 'bg-yellow-400/10', color: 'text-yellow-400' },
  completed: { label: 'Completado', bg: 'bg-green-500/15', color: 'text-green-400' },
};

function StreakCard({ streak }: { streak: StreakView }) {
  const theme = getStreakTheme(streak.type);
  const active = streak.current > 0;
  return (
    <div
      className={`relative rounded-xl p-4 border border-outline-variant/10 bg-gradient-to-br ${theme.bg} overflow-hidden`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-on-surface-variant font-bold">
            {theme.label}
          </div>
          <div className="text-3xl font-extrabold text-on-surface mt-1 flex items-baseline gap-1">
            {streak.current}
            <span className="text-xs font-medium text-on-surface-variant">días</span>
          </div>
        </div>
        <div
          className={`w-10 h-10 rounded-full bg-surface-container-low flex items-center justify-center ${theme.color} ${active ? 'streak-flame' : 'opacity-40'}`}
        >
          <span className="material-symbols-outlined text-[22px]">{theme.icon}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-on-surface-variant">
        <span className="material-symbols-outlined text-[14px]">military_tech</span>
        Mejor: <span className="font-semibold text-on-surface">{streak.longest}</span>
      </div>
      <style>{`
        @keyframes flame {
          0%, 100% { transform: scale(1) rotate(-2deg); }
          50% { transform: scale(1.12) rotate(2deg); }
        }
        .streak-flame { animation: flame 1.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function AchievementCard({ view }: { view: UserAchievementView }) {
  const cfg = STATUS_BADGE[view.status];
  const isLocked = view.status === 'locked';
  const isCompleted = view.status === 'completed';
  const tierTheme = getTierTheme(view.achievement.tier);
  const eventIcon = getEventIcon(view.achievement.rule.event);
  const progressLabel = formatProgressLabel(
    view.achievement.rule,
    view.assignment?.progress ?? null,
    isCompleted,
  );

  return (
    <div
      className={`group relative rounded-2xl p-5 border transition-all duration-300 overflow-hidden shadow-lg ${
        isLocked
          ? 'bg-surface-container/40 border-outline-variant/10 opacity-60'
          : isCompleted
          ? `bg-surface-container-low ${tierTheme.cardBorder} hover:scale-[1.02] ${tierTheme.glow}`
          : `bg-surface-container-low border-outline-variant/15 hover:${tierTheme.cardBorder.replace('border-', 'border-')} hover:-translate-y-0.5`
      }`}
    >
      {/* Locked stripe */}
      {isLocked && (
        <div className="absolute top-0 right-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-bl-lg bg-surface-container-low text-on-surface-variant border-l border-b border-outline-variant/10">
          🔒 Bloqueado
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* Medal icon */}
        <div className="relative shrink-0">
          <div
            className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tierTheme.medalBg} flex items-center justify-center ${tierTheme.medalText} ${isCompleted ? 'medal-shine' : ''} ${isLocked ? 'grayscale opacity-60' : ''}`}
          >
            <span className="material-symbols-outlined text-[26px]">{eventIcon}</span>
          </div>
          {isCompleted && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 border-2 border-surface-container-low flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[14px]">check</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-on-surface text-base leading-tight ${isLocked ? 'pr-20' : ''}`}>
            {view.achievement.title}
          </h3>
          <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">
            {view.achievement.description}
          </p>
        </div>
      </div>

      {!isLocked && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>
              {cfg.label}
            </span>
            <span className="text-[11px] text-on-surface-variant font-medium">
              {progressLabel}
            </span>
          </div>
          <div className="w-full h-1.5 bg-surface-container rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isCompleted
                  ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                  : 'bg-gradient-to-r from-primary to-yellow-400'
              }`}
              style={{ width: `${view.progressPercent}%` }}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes medal-shine {
          0% { transform: rotate(-6deg) scale(1); }
          50% { transform: rotate(6deg) scale(1.06); }
          100% { transform: rotate(-6deg) scale(1); }
        }
        .medal-shine { animation: medal-shine 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function TierHeader({ tier, completed, total }: { tier: number; completed: number; total: number }) {
  const theme = getTierTheme(tier);
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div className="flex items-center gap-4 mb-4">
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-bold text-on-surface flex items-baseline gap-2">
          <span className="text-xl">{theme.emoji}</span>
          Tier {tier}
          <span className="text-sm font-normal text-on-surface-variant">— {theme.label}</span>
        </h2>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 max-w-[200px] h-1.5 bg-surface-container rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${theme.medalBg.replace('from-', 'from-').replace('to-', 'to-')}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-on-surface-variant font-semibold">
            {completed} / {total}
          </span>
        </div>
      </div>
    </div>
  );
}

function TierSection({
  tier,
  achievements,
}: {
  tier: number;
  achievements: UserAchievementView[];
}) {
  const completed = achievements.filter((a) => a.status === 'completed').length;
  return (
    <section className="mb-10">
      <TierHeader tier={tier} completed={completed} total={achievements.length} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {achievements.map((view) => (
          <AchievementCard key={view.achievement.id} view={view} />
        ))}
      </div>
    </section>
  );
}

export default function Achievements() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['achievements', 'me'],
    queryFn: () => achievementsApi.getMine(),
  });

  const grouped = useMemo(() => {
    if (!data) return { tiers: [], byTier: new Map<number, UserAchievementView[]>() };
    const byTier = new Map<number, UserAchievementView[]>();
    for (const view of data.achievements) {
      const list = byTier.get(view.achievement.tier) ?? [];
      list.push(view);
      byTier.set(view.achievement.tier, list);
    }
    const tiers = [...byTier.keys()].sort((a, b) => a - b);
    return { tiers, byTier };
  }, [data]);

  if (isLoading) return <div className="p-6 text-on-surface-variant">Cargando…</div>;
  if (isError) {
    return <div className="p-6 text-error">Error: {(error as Error).message}</div>;
  }
  if (!data) return null;

  const totalCompleted = data.achievements.filter((a) => a.status === 'completed').length;
  const totalUnlocked = data.achievements.filter((a) => a.status !== 'locked').length;
  const currentTierTheme = getTierTheme(data.currentTier);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Hero header */}
      <section className="mb-8 rounded-3xl bg-gradient-to-br from-surface-container-low via-surface-container-low to-surface-container p-6 md:p-8 border border-outline-variant/15 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-2">
              Tu progreso
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-on-surface flex items-center gap-3">
              <span className="text-4xl">{currentTierTheme.emoji}</span>
              Tier {data.currentTier} — {currentTierTheme.label}
            </h1>
            <p className="text-on-surface-variant mt-2 max-w-xl">
              {totalCompleted} de {totalUnlocked} logros desbloqueados.
              Completá los del tier actual para avanzar al siguiente.
            </p>
          </div>

          <div className="flex gap-3">
            <div className="bg-surface-container-low rounded-xl px-4 py-3 border border-outline-variant/10 text-center min-w-[110px]">
              <div className="text-2xl font-extrabold text-on-surface">{totalCompleted}</div>
              <div className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
                Conquistados
              </div>
            </div>
            <div className="bg-surface-container-low rounded-xl px-4 py-3 border border-outline-variant/10 text-center min-w-[110px]">
              <div className="text-2xl font-extrabold text-on-surface">
                {data.achievements.length - totalCompleted}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
                Restantes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Streaks */}
      {data.streaks.length > 0 && (
        <section className="mb-10">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-on-surface-variant mb-3">
            Tus rachas
          </h2>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            {data.streaks.map((streak) => (
              <StreakCard key={streak.type} streak={streak} />
            ))}
          </div>
        </section>
      )}

      {/* Tier sections */}
      {grouped.tiers.map((tier) => (
        <TierSection key={tier} tier={tier} achievements={grouped.byTier.get(tier) ?? []} />
      ))}
    </div>
  );
}
